import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle, FaRedo, FaHome, FaChevronDown, FaChevronUp, FaWrench, FaWifi } from 'react-icons/fa';

// Generic Error Page component that can be used standalone or within ErrorBoundary
export function GenericErrorPage({ error, resetErrorBoundary }) {
  const [showDetails, setShowDetails] = React.useState(false);

  const handleRefresh = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
      <div className="max-w-xl w-full bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-blue-100 p-8 text-center transition-all duration-300 hover:shadow-blue-100/50">
        
        {/* Animated Icon Container */}
        <div className="inline-flex items-center justify-center w-20 h-20 bg-red-50 text-red-500 rounded-full mb-6 animate-pulse">
          <FaExclamationTriangle className="text-4xl" />
        </div>

        <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight mb-3">
          Oops! Something went wrong
        </h1>
        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
          An unexpected error occurred. We've logged this issue and are working to resolve it as quickly as possible.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 font-medium"
          >
            <FaRedo className="animate-spin-hover" />
            Try Again
          </button>
          
          <Link
            to="/"
            onClick={() => resetErrorBoundary && resetErrorBoundary()}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all duration-200 font-medium"
          >
            <FaHome />
            Go to Dashboard
          </Link>
        </div>

        {/* Expandable Debug Details */}
        {error && (
          <div className="border-t border-slate-100 pt-6 text-left">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-between w-full text-slate-500 hover:text-slate-700 font-medium transition-colors focus:outline-none"
            >
              <span>Debug Information</span>
              {showDetails ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {showDetails && (
              <div className="mt-4 p-4 bg-slate-900 rounded-xl border border-slate-800 text-xs font-mono text-rose-400 overflow-x-auto max-h-60 shadow-inner">
                <p className="font-bold text-slate-300 mb-2">Error Message:</p>
                <pre className="mb-4 bg-slate-800 p-2 rounded text-rose-300">{error.message || String(error)}</pre>
                {error.stack && (
                  <>
                    <p className="font-bold text-slate-300 mb-2">Stack Trace:</p>
                    <pre className="text-slate-400 leading-5 whitespace-pre-wrap">{error.stack}</pre>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// React Class Error Boundary
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught runtime crash:', error, errorInfo);
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <GenericErrorPage
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }
    return this.props.children;
  }
}

// Maintenance Mode Page
export function MaintenancePage() {
  const [checking, setChecking] = React.useState(false);
  const [status, setStatus] = React.useState(null); // 'online', 'offline', or null
  const [maintenanceMessage, setMaintenanceMessage] = React.useState('');

  React.useEffect(() => {
    const fetchMessage = async () => {
      try {
        const apiDomain = process.env.REACT_APP_API_DOMAIN || 'https://myapi-dev-226556767471.asia-south1.run.app';
        const response = await fetch(`${apiDomain}/api/v2/appsettings/get`, {
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.maintenanceMessage) {
            setMaintenanceMessage(data.maintenanceMessage);
          }
        }
      } catch (e) {
        console.error('Failed to fetch maintenance message:', e);
      }
    };
    fetchMessage();
  }, []);

  const checkSystemStatus = async () => {
    setChecking(true);
    setStatus(null);
    try {
      // Import apiBase/endpoints to do a health check
      const apiDomain = process.env.REACT_APP_API_DOMAIN || 'https://myapi-dev-226556767471.asia-south1.run.app';
      const response = await fetch(`${apiDomain}/api/v2/appsettings/get`, {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.maintenanceMode) {
          setStatus('offline');
        } else {
          setStatus('online');
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        }
      } else {
        setStatus('offline');
      }
    } catch (e) {
      setStatus('offline');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-6">
      <div className="max-w-xl w-full bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-indigo-100 p-8 text-center transition-all duration-300 hover:shadow-indigo-100/50">
        
        {/* Gear Icon with subtle bounce animation */}
        <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full mb-6">
          <FaWrench className="text-4xl animate-bounce" />
        </div>

        <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight mb-3">
          System Maintenance
        </h1>
        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
          {maintenanceMessage || "We are currently upgrading our systems to serve you better. Namasvi is expected to be back online shortly. Thank you for your patience!"}
        </p>

        {/* Visual Pulse Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8 bg-indigo-50/50 py-2.5 px-4 rounded-full w-fit mx-auto border border-indigo-100">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
          </span>
          <span className="text-indigo-700 text-sm font-semibold">Undergoing Scheduled Updates</span>
        </div>

        {/* Interaction Actions */}
        <div className="space-y-4">
          <button
            onClick={checkSystemStatus}
            disabled={checking}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-indigo-400 disabled:to-blue-400 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 disabled:transform-none transition-all duration-200"
          >
            {checking ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                Checking Connection...
              </>
            ) : (
              <>
                <FaWifi />
                Check System Status
              </>
            )}
          </button>
        </div>

        {/* Status Notification Alerts */}
        {status === 'online' && (
          <div className="mt-6 p-4 bg-green-50 text-green-800 border border-green-200 rounded-xl text-sm font-medium flex items-center justify-center gap-2 animate-pulse">
            <span>🎉</span> Connection Restored! Returning to dashboard...
          </div>
        )}

        {status === 'offline' && (
          <div className="mt-6 p-4 bg-red-50 text-red-800 border border-red-200 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
            <span>⚠️</span> The server is still undergoing maintenance. Please try again in a few minutes.
          </div>
        )}
      </div>
    </div>
  );
}
